/**
 * ABC-S classes and lead times (docs/24 §3.2).
 *
 * The kick-off notes put every material into one of four buckets, and the
 * bucket decides how early it has to be bought:
 *
 *   A  high value, or a long lead time      a water tank — two to three months
 *   B  medium value, or a medium lead time  valves and pipes — 15 to 20 days
 *   C  low value, short lead time           fittings — 2 to 3 days, or local
 *   S  special specification or customised  whatever the customer specified
 *
 * A, B and C can be worked out from the numbers; S cannot — it is a decision
 * about a specification, and is never suggested, only assigned.
 *
 * Pure functions only, so the server, the reports and the tests all classify
 * a material the same way.
 */

export const ABC_CLASSES = ['A', 'B', 'C', 'S'] as const
export type AbcClass = (typeof ABC_CLASSES)[number]

export const ABC_OPTIONS: ReadonlyArray<[AbcClass, string]> = [
  ['A', 'A — high value or long lead time'],
  ['B', 'B — medium value or lead time'],
  ['C', 'C — low value, buy locally'],
  ['S', 'S — special or customised'],
]

export const isAbcClass = (v: unknown): v is AbcClass =>
  typeof v === 'string' && (ABC_CLASSES as readonly string[]).includes(v)

/** The company's thresholds, read from settings. */
export interface AbcThresholds {
  /** Class A is the materials making up the first this-many percent of value. */
  readonly aValueShare: number
  /** Class B runs on to this cumulative percentage; the rest are C. */
  readonly bValueShare: number
  /** A lead time at least this long makes a material A whatever it costs. */
  readonly aLeadDays: number
  /** And at least this long, B. */
  readonly bLeadDays: number
}

export const DEFAULT_THRESHOLDS: AbcThresholds = {
  aValueShare: 70, bValueShare: 90, aLeadDays: 45, bLeadDays: 10,
}

/** The lead time a class implies when neither the item nor its category says. */
export interface ClassLeadTimes {
  readonly A: number
  readonly B: number
  readonly C: number
  readonly S: number
}

export const DEFAULT_CLASS_LEAD_TIMES: ClassLeadTimes = { A: 75, B: 20, C: 3, S: 90 }

const RANK: Readonly<Record<'A' | 'B' | 'C', number>> = { A: 0, B: 1, C: 2 }

/** The more demanding of two classes: A outranks B outranks C. */
export const higherClass = (a: 'A' | 'B' | 'C', b: 'A' | 'B' | 'C'): 'A' | 'B' | 'C' =>
  RANK[a] <= RANK[b] ? a : b

/**
 * The class a material's place in the value ranking puts it in.
 *
 * `shareBefore` is the cumulative share of everything ranked above it. The
 * material that carries the total across a threshold belongs to the class it
 * crosses from — otherwise one very expensive item on a small job, 90 % of the
 * value on its own, would come out B.
 */
export const classByValue = (shareBefore: number, t: AbcThresholds = DEFAULT_THRESHOLDS): 'A' | 'B' | 'C' =>
  shareBefore < t.aValueShare ? 'A' : shareBefore < t.bValueShare ? 'B' : 'C'

/** The class a lead time alone puts a material in. Unknown is no demand at all. */
export const classByLeadTime = (days: number | null, t: AbcThresholds = DEFAULT_THRESHOLDS): 'A' | 'B' | 'C' =>
  days === null ? 'C' : days >= t.aLeadDays ? 'A' : days >= t.bLeadDays ? 'B' : 'C'

/** "High value OR high lead time": whichever of the two is more demanding. */
export const suggestClass = (
  shareBefore: number, leadDays: number | null, t: AbcThresholds = DEFAULT_THRESHOLDS,
): 'A' | 'B' | 'C' => higherClass(classByValue(shareBefore, t), classByLeadTime(leadDays, t))

export type LeadTimeSource = 'ITEM' | 'CATEGORY' | 'CLASS' | 'NONE'

export interface EffectiveLeadTime {
  readonly days: number | null
  readonly source: LeadTimeSource
}

const days = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null
}

/**
 * How long a material takes to arrive once ordered.
 *
 * The item's own figure, else its category's, else what its class implies.
 * A material with none of the three has no lead time — which the MRP reads as
 * "order it when it is needed", and says so rather than inventing a number.
 */
export const effectiveLeadTime = (
  input: {
    readonly itemDays?: unknown
    readonly categoryDays?: unknown
    readonly abcClass?: unknown
  },
  classDays: ClassLeadTimes = DEFAULT_CLASS_LEAD_TIMES,
): EffectiveLeadTime => {
  const own = days(input.itemDays)
  if (own !== null) return { days: own, source: 'ITEM' }
  const category = days(input.categoryDays)
  if (category !== null) return { days: category, source: 'CATEGORY' }
  if (isAbcClass(input.abcClass)) return { days: classDays[input.abcClass], source: 'CLASS' }
  return { days: null, source: 'NONE' }
}

/** Planning settings, read out of the company's settings map with defaults. */
export const planningSettings = (
  settings: Readonly<Record<string, boolean | number | string>>,
): { thresholds: AbcThresholds; classDays: ClassLeadTimes } => {
  const n = (key: string, fallback: number): number => {
    const v = Number(settings[key])
    return Number.isFinite(v) && v >= 0 ? v : fallback
  }
  return {
    thresholds: {
      aValueShare: n('abc_a_value_share', DEFAULT_THRESHOLDS.aValueShare),
      bValueShare: n('abc_b_value_share', DEFAULT_THRESHOLDS.bValueShare),
      aLeadDays: n('abc_a_lead_days', DEFAULT_THRESHOLDS.aLeadDays),
      bLeadDays: n('abc_b_lead_days', DEFAULT_THRESHOLDS.bLeadDays),
    },
    classDays: {
      A: n('lead_time_a_days', DEFAULT_CLASS_LEAD_TIMES.A),
      B: n('lead_time_b_days', DEFAULT_CLASS_LEAD_TIMES.B),
      C: n('lead_time_c_days', DEFAULT_CLASS_LEAD_TIMES.C),
      S: n('lead_time_s_days', DEFAULT_CLASS_LEAD_TIMES.S),
    },
  }
}
